import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, TrendingUp, AlertCircle } from "lucide-react";

export default function Recruitment() {
  const { data: recruitmentRecords = [], isLoading } = useQuery({
    queryKey: ["recruitment-tracking"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recruitment_tracking")
        .select("*")
        .order("contact_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const stats = {
    total: recruitmentRecords.length,
    compliant: recruitmentRecords.filter((r) => r.compliance_status === "compliant").length,
    flagged: recruitmentRecords.filter((r) => r.compliance_status === "flagged").length,
    underReview: recruitmentRecords.filter((r) => r.compliance_status === "under_review").length,
    nilDiscussions: recruitmentRecords.filter((r) => r.nil_discussion_occurred).length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "compliant": return "default";
      case "flagged": return "destructive";
      case "under_review": return "secondary";
      default: return "outline";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8 text-primary" />
            Recruitment Compliance Tracking
          </h1>
          <p className="text-muted-foreground mt-2">
            Monitor recruiting contacts and NIL discussions for NCAA compliance
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Recruitment activities</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliant</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.compliant}</div>
              <p className="text-xs text-muted-foreground">Within NCAA rules</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">NIL Discussions</CardTitle>
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.nilDiscussions}</div>
              <p className="text-xs text-muted-foreground">NIL mentioned</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Flagged</CardTitle>
              <AlertCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.flagged}</div>
              <p className="text-xs text-muted-foreground">Needs review</p>
            </CardContent>
          </Card>
        </div>

        {/* Recruitment Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recruitment Activity Log</CardTitle>
            <CardDescription>All recruiting contacts and communications</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading recruitment data...</div>
            ) : recruitmentRecords.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No recruitment records found. Demo data may not be loaded.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prospect</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Grad Year</TableHead>
                    <TableHead>Contact Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Staff Member</TableHead>
                    <TableHead>NIL Discussed</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recruitmentRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.prospect_name}</TableCell>
                      <TableCell>{record.sport}</TableCell>
                      <TableCell>{record.graduation_year}</TableCell>
                      <TableCell className="capitalize">{record.contact_type.replace('_', ' ')}</TableCell>
                      <TableCell>{new Date(record.contact_date).toLocaleDateString()}</TableCell>
                      <TableCell>{record.staff_member_name}</TableCell>
                      <TableCell>
                        {record.nil_discussion_occurred ? (
                          <Badge variant="secondary">Yes</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(record.compliance_status)}>
                          {record.compliance_status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
